import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApprovalDecisionModal } from "@/components/approvals/ApprovalDecisionModal";
import { ApprovalsFilters, type ApprovalsFiltersState } from "@/components/approvals/ApprovalsFilters";
import { ApprovalsTable } from "@/components/approvals/ApprovalsTable";
import { Button } from "@/components/ui/button";
import { useApprovalsList } from "@/lib/hooks/approvals";
import { useCurrentSession } from "@/lib/session";
import type { ApprovalsListFilters } from "@/lib/schemas/approvals";

interface SearchParams {
  highlight?: string;
}

export const Route = createFileRoute("/_app/approvals")({
  component: ApprovalsPage,
  validateSearch: (search): SearchParams => ({
    highlight: typeof search.highlight === "string" ? search.highlight : undefined,
  }),
});

const DEFAULT_FILTERS: ApprovalsFiltersState = {
  status: "pending",
  forMe: false,
};

function ApprovalsPage(): JSX.Element {
  const { t } = useTranslation();
  const { highlight } = Route.useSearch();
  const sessionQuery = useCurrentSession();
  const currentUserId = sessionQuery.data?.user_id;
  const mfaAssured =
    sessionQuery.data?.mfa_assured_until !== null &&
    sessionQuery.data?.mfa_assured_until !== undefined &&
    new Date(sessionQuery.data.mfa_assured_until).getTime() > Date.now();

  const [filters, setFilters] = useState<ApprovalsFiltersState>(DEFAULT_FILTERS);
  const [selectedId, setSelectedId] = useState<string | undefined>(highlight);
  const [cursor, setCursor] = useState<string | undefined>();

  const apiFilters: ApprovalsListFilters = useMemo(() => {
    // Send `status=all` explicitly (not omit) so the BE bypasses its default
    // "pending + non-expired" filter — otherwise picking "Tutti gli stati"
    // would behave identically to "pending" (vedi ApprovalRepo.list_*).
    const base: ApprovalsListFilters = { limit: 25, status: filters.status };
    if (filters.forMe) base.for_me = true;
    if (cursor) base.cursor = cursor;
    return base;
  }, [filters, cursor]);

  const query = useApprovalsList(apiFilters);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">{t("nav.approvals")}</h1>
          {query.data?.total_estimate !== undefined && (
            <p className="text-sm text-muted-foreground">
              {t("approvals.list.total", { count: query.data.total_estimate })}
            </p>
          )}
        </div>
      </header>

      <ApprovalsFilters
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setCursor(undefined);
        }}
      />

      {query.error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          {t("approvals.list.load_error")} — {query.error.message}
        </div>
      )}

      <ApprovalsTable
        approvals={query.data?.data ?? []}
        isLoading={query.isLoading}
        currentUserId={currentUserId}
        selectedId={selectedId}
        onRowClick={(a) => setSelectedId(a.id)}
      />

      {query.data && (query.data.next_cursor || query.data.prev_cursor) && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.prev_cursor}
            onClick={() => setCursor(query.data.prev_cursor ?? undefined)}
          >
            ← {t("common.back")}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!query.data.next_cursor}
            onClick={() => setCursor(query.data.next_cursor ?? undefined)}
          >
            {t("common.next")} →
          </Button>
        </div>
      )}

      <ApprovalDecisionModal
        approvalId={selectedId}
        currentUserId={currentUserId}
        mfaAssured={mfaAssured}
        onClose={() => setSelectedId(undefined)}
      />
    </div>
  );
}
